import { DEFAULT_PROPOSAL_NAME, IProposal } from '../models/proposals.model';
import {
  ArrayOrSingle,
  ItemOrItemId,
  OptionallyRequired,
  OptionallyRequiredOptions,
  SemiPartial,
} from '../models/type-helpers.model';
import { mapDuplicateElementIdRecordKeys } from './actions/clean-actions.helpers';
import { getId, omitUndefined, replaceProperties } from './object.helpers';
import {
  Baseline,
  IElement,
  OneOfElementListElements,
  OneOfElements,
  OneOfParentElements,
} from '../models/project.model';
import { cacheFactory, required } from './function.helpers';
import { getEmptyFrozenArray } from './array.helpers';
import {
  forEachElement,
  isBuildingVersionElement,
  isElement,
} from './recursive-element.helpers';
import { IElementVersionMap } from './element-factory.helpers';
import {
  BASELINE_COLOR,
  PROPOSAL_COLORS,
} from './proposals/proposal-colors.constants';
import { createId, type RegenerateIds } from './id.helpers';
import { getResolvedBuildingVersion } from './actions/resolved-version.helpers';
import { getResolvedVersionCacheProposalKey } from './actions/resolved-version-enrich-cache.helpers';

/**
 * Create a new proposal. Pass existing proposal to duplicate.
 *
 * @param defaults - Partial proposal row; `id` is honoured when {@link regenerateIds} is false.
 * @param maps - When duplicating a version, maps previous element ids for {@link mapRecordKeys} on `resultsRecord`.
 * @param regenerateIds - Passed to {@link createId} for `id` (default regenerates when missing or when forced).
 */
export const createProposal = (
  defaults: Partial<IProposal>,
  maps?: IElementVersionMap,
  regenerateIds: RegenerateIds = true,
): IProposal => {
  const resultsRecord = mapDuplicateElementIdRecordKeys(
    defaults.resultsRecord ?? {},
    maps?.elementMap ?? new Map(),
    maps?.sourceBaselineElementIds,
  );

  return {
    name: DEFAULT_PROPOSAL_NAME,
    ...omitUndefined(defaults),
    actionIds: defaults.actionIds ?? [],
    colorIndex: defaults.colorIndex ?? 0,
    id: createId(defaults, regenerateIds),
    resultsRecord,
  };
};

export const updateElementActiveVersionStates = (version: Baseline) => version;

export const getProposalsInVersion = (
  versionOrProposals: IProposal[] | Baseline | undefined,
): IProposal[] => {
  const proposals = Array.isArray(versionOrProposals)
    ? versionOrProposals
    : (versionOrProposals?.proposals ?? getEmptyFrozenArray());

  return proposals;
};

/**
 * Get proposal by id, crash if not found
 * @param version
 * @param proposalOrId
 * @returns
 */
export const getProposalById = <E extends OptionallyRequiredOptions = true>(
  version: Baseline,
  proposalOrId: OptionallyRequired<ItemOrItemId<IProposal>, E>,
  requireProposal: E = true as E,
) => {
  const id = proposalOrId ? getId(proposalOrId) : undefined;
  return required(
    getProposalsInVersion(version).find((p) => p.id === id),
    requireProposal === true ? 'Could not find proposal' : requireProposal,
  );
};

/**
 * Update proposal with new values (partial).
 * Will not update unsless anything have changed.
 * @param version
 * @param proposal
 * @returns
 */
export const updateProposals = (
  version: Baseline,
  ...updates: SemiPartial<IProposal, 'id'>[]
): Baseline => {
  const currentProposals = getProposalsInVersion(version);

  if (updates.some(({ id }) => !currentProposals.some((p) => p.id === id))) {
    throw new Error('Proposal not found');
  }

  const proposals = currentProposals.map((p) => {
    const update = updates.find((u) => u.id === p.id);
    return update ? replaceProperties(p, update) : p;
  });

  return replaceProperties(version, { proposals }, true);
};

export const deleteProposal = (
  version: Baseline,
  proposalOrId: ItemOrItemId<IProposal>,
): Baseline => {
  const id = getId(proposalOrId);
  const proposals = getProposalsInVersion(version);
  if (proposals.length <= 1) return version;

  const updatedVersion = {
    ...version,
    proposals: proposals.filter((p) => p.id !== id),
  };
  return versionWithNormalizedProposalsColors(updatedVersion);
};

/**
 * Add element to proposal
 * @param version
 * @param proposalsOrId
 * @param element
 * @param skipCleanup If true, the cleanup of obsolete element versions will be skipped
 * @returns
 */
export const selectElementByProposals = (
  version: Baseline,
  _proposalsOrId: ArrayOrSingle<ItemOrItemId<IProposal>>,
  _elementOrId: ItemOrItemId<IElement>,
  _skipCleanup = false,
): Baseline => version;

/**
 * Check if an element is ACTIVELY selected in a proposal, not to be confused with not selected in any proposals.
 * Note: Does not consider parent selections (like if a parent is selected/deselected)
 * @param proposal.
 * @param elementOrId
 * @returns
 */
export const isSelectedByProposal = (
  _proposal: IProposal | undefined,
  _elementOrId: ItemOrItemId<OneOfElementListElements>,
): boolean => false;

/**
 * Add element version to to proposals without selections
 * @param version
 * @param element
 * @returns An updated version with the new proposal selections
 */
export const addElementVersionToUnselectedProposals = (
  version: Baseline,
  _element: IElement,
): Baseline => version;

/**
 * Deselect element by proposals.
 * @param version
 * @param proposalOrIds
 * @param element
 * @param addVersionToDeselect If true, we will make element to a version in order to remove it from proposals
 * @returns
 */
export const deselectElementByProposals = (
  version: Baseline,
  _proposalOrIds: ArrayOrSingle<ItemOrItemId<IProposal>>,
  _elementOrId: ItemOrItemId<IElement>,
  _addVersionToDeselect = true,
): Baseline => version;

export const removeElementFromAllProposals = (
  version: Baseline,
  _element: IElement,
): Baseline => version;

/**
 * Maps a stored proposal color index to the corresponding {@link PROPOSAL_COLORS} entry (with modulo).
 *
 * @param colorIndex - Persisted or effective {@link IProposal.colorIndex}
 * @returns Hex color for charts, proposal bars, and list swatches
 */
export const getProposalSwatchColor = (colorIndex: number): string => {
  const n = PROPOSAL_COLORS.length;
  const idx = ((colorIndex % n) + n) % n;
  return PROPOSAL_COLORS[idx] ?? BASELINE_COLOR;
};

export const getProposalColor = (
  proposals: IProposal[],
  proposalOrId?: ItemOrItemId<IProposal>,
  fallbackColor = BASELINE_COLOR,
): string => {
  if (proposalOrId === undefined) return fallbackColor;
  const id = getId(proposalOrId);
  const colorIndex = proposals.find((p) => p.id === id)?.colorIndex;
  return getProposalSwatchColor(colorIndex ?? 0);
};

export const isEmptyProposal = (proposal: IProposal): boolean => {
  return proposal.actionIds.length === 0;
};

/**
 * Get all proposals that are available to an element
 * @param proposals All proposals in version
 * @param path Path to element (from root to element)
 * @returns
 */
export const getAvailableProposals = (
  proposals: IProposal[] | undefined,
  _path: OneOfParentElements[],
): IProposal[] => proposals ?? [];

/**
 * Get all proposals explicitly selecting the element.
 * Legacy proposal selection lookup kept as a no-op while old call sites are removed.
 * @param proposals
 * @param element
 * @returns
 */
export const getProposalsWithElementSelected = (
  _proposals: IProposal[],
  _element: OneOfElementListElements,
): IProposal[] => [];

/**
 * Check if an element is active in a proposal
 * @param proposal
 * @param path
 * @param element
 * @returns
 */
export const isElementActiveInProposal = (
  proposal: IProposal | undefined,
  path: OneOfParentElements[],
  element: OneOfElementListElements,
): boolean => {
  // First check cases that can be resolved without checking parents
  if (isBuildingVersionElement(element)) {
    return true;
  }

  if (isElement(element)) {
    // Disabled by eye icon or deleted from the resolved proposal view
    if (element.isDeactivated || element.isDeleted) {
      return false;
    }
  }

  // Check if some parent is deactivated
  const hasDeactivatedParent = path.some(
    (parent) => !isElementActiveInProposal(proposal, [], parent),
  );

  return !hasDeactivatedParent;
};

/**
 * Get all elements that will be active when toggling a proposal active.
 * This will return all all selected element but also elements that have no selection by any proposal.
 * Will also remove all children of deactivated elements.
 *
 * Membership is evaluated on {@link getResolvedBuildingVersion} for that proposal so
 * action-applied deactivation matches record-mode UI (baseline `isDeactivated` alone is not enough).
 *
 * @param version
 * @param proposalOrId Provide proposal or an id else you will get the active proposal
 * @param requireProposal If true (default), it will throw an error if the proposal is not found
 * @returns
 */
/**
 * Active root element ids ({@link Baseline.elements}) for a proposal.
 * Uses the proposal-resolved tree for deactivation — same rules as
 * {@link getElementsInProposal}, without walking the full subtree.
 */
export const getActiveRootElementIdsInProposal = (
  version: Baseline,
  proposalOrId?: ItemOrItemId<IProposal>,
  requireProposal = true,
): ReadonlySet<string> => {
  const proposal = proposalOrId
    ? getProposalById(version, proposalOrId, requireProposal)
    : undefined;

  const treeForMembership = proposal
    ? getResolvedBuildingVersion(version, proposal)
    : version;

  return cacheFactory(
    () => {
      const ids = new Set<string>();
      for (const child of treeForMembership.elements) {
        if (!isElement(child)) continue;
        if (isElementActiveInProposal(proposal, [treeForMembership], child)) {
          ids.add(child.id);
        }
      }
      return ids;
    },
    `getActiveRootElementIdsInProposal[${version.id}, ${getResolvedVersionCacheProposalKey(proposalOrId)}]`,
    // Store replaces version/proposals immutably (no in-place proposal edits); id + actions suffice.
    [
      version,
      getResolvedVersionCacheProposalKey(proposalOrId),
      version.actions,
    ],
  );
};

export const getElementsInProposal = (
  version: Baseline,
  proposalOrId?: ItemOrItemId<IProposal>,
  requireProposal = true,
): OneOfElements[] => {
  const proposal = proposalOrId
    ? getProposalById(version, proposalOrId, requireProposal)
    : undefined;

  // Walk the proposal-resolved tree so disable/delete actions affect
  // {@link isElementActiveInProposal} the same way as record-mode UI.
  const treeForMembership = proposal
    ? getResolvedBuildingVersion(version, proposal)
    : version;

  // Only run this once per version and proposal
  return cacheFactory(
    () => {
      const elements: OneOfElements[] = [];

      forEachElement(treeForMembership, (element, path) => {
        if (isElementActiveInProposal(proposal, path, element)) {
          elements.push(element);
        }
      });
      return elements;
    },
    `getElementsActiveInProposal[${version.id}, ${getResolvedVersionCacheProposalKey(proposalOrId)}]`,
    // Store replaces version/proposals immutably (no in-place proposal edits); id + actions suffice.
    [
      version,
      getResolvedVersionCacheProposalKey(proposalOrId),
      version.actions,
    ],
  );
};

/**
 * Create a new element version and select it in the active proposal
 * @param version
 * @param original
 * @returns
 */
export const addElementVersionAndUpdateProposalSelections = (
  version: Baseline,
  _original: IElement,
): Baseline => version;

/**
 * Get the elementId selected by the proposal
 * @param proposal
 * @param elementOrVersionId
 * @returns An element id if selected, otherwise undefined
 */
export const getSelectedElementId = (
  _proposal: IProposal | undefined,
  _elementOrVersionId: string,
): IElement['id'] | undefined => undefined;

/**
 * Smallest non-negative integer not present in {@link usedIndices}.
 * Used when normalizing legacy rows and when picking the next free palette slot.
 *
 * @param usedIndices - Indices already taken in the version (or reserved for grey-row normalization)
 */
export const findNextPreferredProposalColorIndex = (
  usedIndices: ReadonlySet<number>,
): number => {
  let idx = 0;
  while (usedIndices.has(idx)) idx++;
  return idx;
};

/**
 * Color index for a new proposal appended after {@link proposals}, accounting for rows
 * that still have {@link IProposal.colorIndex} 0 and will consume slots 0..k-1 when normalized.
 *
 * @param proposals - Existing rows before the append
 */
export const getNextColorIndexForAppendedProposal = (
  proposals: readonly IProposal[],
): number => {
  const greyRowCount = proposals.filter((p) => p.colorIndex === 0).length;
  const usedIndices = new Set<number>();
  for (const p of proposals) {
    if (p.colorIndex !== undefined && p.colorIndex !== 0) {
      usedIndices.add(p.colorIndex);
    }
  }
  // Existing grey rows normalize to consecutive indices starting at 0 in list order
  for (let slot = 0; slot < greyRowCount; slot++) {
    usedIndices.add(slot);
  }
  return findNextPreferredProposalColorIndex(usedIndices);
};

/**
 * Per-array WeakMap memo for {@link buildProposalEffectiveColorIndexMap}.
 * Since the result is fully derived from the input array (and immutable), every
 * consumer that holds the same `proposals` reference can share one map. This
 * avoids recomputing in the many UI hooks ({@link useProposalColor},
 * {@link Proposals}, element row colors, kebab menus) that all call the helper
 * independently per render.
 */
const proposalColorIndexMapCache = new WeakMap<
  readonly IProposal[],
  ReadonlyMap<string, number>
>();

const computeProposalEffectiveColorIndexMap = (
  proposals: readonly IProposal[],
): ReadonlyMap<string, number> => {
  const map = new Map<string, number>();
  const reserved = new Set<number>();
  for (const p of proposals) {
    if (p.colorIndex !== 0) reserved.add(p.colorIndex);
  }
  for (const p of proposals) {
    if (p.colorIndex !== 0) {
      map.set(p.id, p.colorIndex);
      continue;
    }
    const idx = findNextPreferredProposalColorIndex(reserved);
    reserved.add(idx);
    map.set(p.id, idx);
  }
  return map;
};

/**
 * Effective palette index per {@link IProposal.id} for the same remapping as
 * {@link normalizeProposalsColorIndices}, without mutating or copying rows.
 * Use in the UI when project data may not have been saved through enrichment yet.
 *
 * Memoized by `proposals` array reference via a {@link WeakMap}; consumers that
 * hold the same array share the same map.
 *
 * @param proposals - All proposal rows for one building version (same order as overview)
 */
export const buildProposalEffectiveColorIndexMap = (
  proposals: readonly IProposal[],
): ReadonlyMap<string, number> => {
  const cached = proposalColorIndexMapCache.get(proposals);
  if (cached) return cached;
  const map = computeProposalEffectiveColorIndexMap(proposals);
  proposalColorIndexMapCache.set(proposals, map);
  return map;
};

/**
 * Remaps {@link IProposal} rows still using colorIndex 0 (legacy / unassigned) to distinct
 * persisted indices in list order, starting at the lowest free non-negative slot.
 * Persist via {@link versionWithNormalizedProposalsColors} when the proposal list changes
 * (e.g. after appending a proposal via the pipeline `addProposal` mutator, {@link deleteProposal})
 * or when {@link migrateProposalsInVersion} runs.
 *
 * @param proposals - Current proposal rows for a building version
 */
export const normalizeProposalsColorIndices = (
  proposals: IProposal[],
): IProposal[] => {
  if (!proposals.some((p) => p.colorIndex === 0)) return proposals;

  const effectiveById = buildProposalEffectiveColorIndexMap(proposals);

  return proposals.map((p) => {
    if (p.colorIndex !== 0) return p;
    const idx = effectiveById.get(p.id);
    if (idx === undefined) return p;
    return { ...p, colorIndex: idx };
  });
};

/**
 * Writes normalized palette indices onto the version when {@link Baseline.proposals} exists.
 *
 * @param version - Building version that may carry {@link Baseline.proposals}
 */
export const versionWithNormalizedProposalsColors = (
  version: Baseline,
): Baseline => {
  const proposals = version.proposals;
  if (!proposals.length) return version;
  const normalized = normalizeProposalsColorIndices(proposals);
  if (normalized === proposals) return version;
  return replaceProperties(version, { proposals: normalized });
};
